/**
 * Kanban Contact Card Component
 * 
 * Specialized version of ContactCard for use in kanban boards.
 * Displays contact information in a card format optimized for kanban layout.
 * 
 * Key Features:
 * - Contact detail navigation (click to open contact page)
 * - Notes navigation (notes button in bottom action bar)
 * - AI summarization (chat button in bottom action bar sends contact notes to AI for analysis)
 * - Keyword highlighting for search results
 * - Contact photo display with fallback
 * - Type chip display (RLP/Team classification) in bottom action bar
 * - Compact layout optimized for kanban columns
 * - No delete functionality (kanban is view-only)
 * - No drag-to-chat (handled by kanban drag system)
 * 
 * Layout:
 * - Content: Contact photo and details in main area
 * - Footer: Tags and action buttons
 * 
 * Props:
 * - contact: Contact object with contact details and noteCount
 * - keywords: Array of strings for highlighting search matches
 */

import { FC } from 'react';
import { useNavigate } from 'react-router';
import { Contact } from '../../models/Contact';
import { THEME_CLASSES } from '../../constants/themeConstants';
import { getContactPhotoUrl } from '../../utils/avatarUtils';
import { useEventBus } from '../../context/EventBusContext';
import { getNotesSummaryPrompt } from '../../utils/summaryUtils';
import { highlightText } from '../../utils/highlightUtils';
import { request } from '../../services/dataService';
import { API_ENDPOINTS } from '../../constants/apiEndpoints';
import { useTeams } from '../../context/TeamContext';
import { getContactCardDisplayFields, getContactFieldValue } from '../../utils/contactUtils';
import { renderTypeChip } from '../../utils/typeChipUtils';

interface KanbanContactCardProps {
  contact: Contact;
  keywords: string[];
}

const KanbanContactCard: FC<KanbanContactCardProps> = ({ contact, keywords }) => {
  const navigate = useNavigate();
  const { publish } = useEventBus();
  const { currentUserId } = useTeams();

  const handleNotesClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`/contacts/${contact.id}/notes`);
  };

  const sendNotesToChat = async () => {
    try {
      const notes = await request(API_ENDPOINTS.GET_CONTACT_NOTES, { userId: currentUserId, id: contact.id });
      if (!notes || !notes.length) return;
      const prompt = getNotesSummaryPrompt(notes);
      publish('chat:open', prompt);
    } catch (error) {
      console.error('Failed to fetch contact notes:', error);
    }
  };

  const handleChatClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    sendNotesToChat();
  };

  // Get only the most important fields for kanban display
  const displayFields = getContactCardDisplayFields().slice(0, 3); // Limit to first 3 fields

  return (
    <div
      className={`p-3 rounded-lg ${THEME_CLASSES.card.background} border ${THEME_CLASSES.card.border} ${THEME_CLASSES.card.hover.full} cursor-pointer flex flex-col min-h-[180px]`}
      onClick={() => navigate(`/contacts/${contact.id}`)}
    >
      <div className="flex items-start gap-3 flex-grow">
        {/* Contact Photo */}
        <div className="flex-shrink-0">
          <div className={`w-16 h-16 rounded-lg border-2 ${THEME_CLASSES.card.border} overflow-hidden`}>
            <img
              src={getContactPhotoUrl(contact)}
              alt={contact.displayName}
              className="w-full h-full object-cover bg-gray-300 dark:bg-gray-600"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.src = '';
                target.className = 'w-full h-full bg-gray-300 dark:bg-gray-600 flex items-center justify-center';
              }}
            />
          </div>
        </div>

        {/* Contact Details */}
        <div className="flex-1 min-w-0 space-y-1">
          <h3 className={`text-sm font-semibold ${THEME_CLASSES.text.primary} break-words`}>
            {keywords.length ? highlightText(contact.displayName, keywords) : contact.displayName}
          </h3>

          <div className="text-xs space-y-1">
            {displayFields.map(field => {
              const value = getContactFieldValue(contact, field);
              if (field.hideEmpty && !value) {
                return null;
              }

              const displayValue = value ?? '-';

              return (
                <div key={field.key} className="flex justify-between items-start gap-2">
                  <span className={`${THEME_CLASSES.text.secondary} shrink-0 text-xs`}>{field.label}:</span>
                  <span className={`${THEME_CLASSES.text.secondary} text-right text-xs break-words`}>
                    {keywords.length && displayValue !== '-'
                      ? highlightText(displayValue.toString(), keywords)
                      : displayValue}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Footer - Tags and actions */}
      <div className="flex items-center justify-between pt-2 mt-2 border-t border-gray-200 dark:border-gray-600">
        <div className="flex items-center gap-1">
          {renderTypeChip({
            rlpCompanyId: 0,
            teamId: contact.teamId,
            rlpPersonId: contact.rlpPersonId ? Number(contact.rlpPersonId) : undefined
          })}
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={handleNotesClick}
            className={`${THEME_CLASSES.button.iconSmall} p-1`}
            title="View notes"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className={`h-3 w-3 ${THEME_CLASSES.text.secondary}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
            </svg>
          </button>
          <button
            onClick={handleChatClick}
            className={`${THEME_CLASSES.button.iconSmall} p-1`}
            title="Ask AI about this contact"
            disabled={contact.noteCount === 0}
          >
            <svg className={`w-3 h-3 ${THEME_CLASSES.text.secondary}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default KanbanContactCard;